import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  FileCheck2,
  Shield,
  Clock,
  CheckCircle2,
  MessageCircle,
  Award,
  Microscope,
  ClipboardCheck,
  AlertTriangle,
  Phone,
} from "lucide-react";

const pmocServices = [
  {
    name: "Análise da Qualidade do Ar",
    description: "Monitoramento contínuo dos parâmetros de qualidade do ar interno conforme normas ANVISA.",
    icon: Microscope,
    gradient: "from-blue-500 to-cyan-500",
    benefits: [
      "Medição de CO₂, temperatura e umidade",
      "Análise microbiológica do ar",
      "Relatórios técnicos detalhados",
      "Conformidade com RE nº 09/2003"
    ]
  },
  {
    name: "Manutenção Preventiva",
    description: "Cronograma estruturado de manutenções para garantir o funcionamento ideal dos equipamentos.",
    icon: Shield,
    gradient: "from-green-500 to-emerald-500",
    benefits: [
      "Limpeza e desinfecção dos componentes",
      "Verificação de filtros e serpentinas",
      "Calibração de equipamentos",
      "Prevenção de falhas e paradas"
    ]
  },
  {
    name: "Documentação Completa",
    description: "Toda documentação necessária para atender às exigências legais e fiscalizações.",
    icon: FileCheck2,
    gradient: "from-purple-500 to-violet-500",
    benefits: [
      "Livro de Registro de Ocorrências",
      "Certificados de limpeza",
      "Laudos técnicos",
      "Cronograma de atividades"
    ]
  },
  {
    name: "Suporte Técnico 24/7",
    description: "Atendimento especializado disponível a qualquer momento para emergências.",
    icon: Clock,
    gradient: "from-orange-500 to-red-500",
    benefits: [
      "Plantão técnico permanente",
      "Resposta rápida a emergências",
      "Suporte remoto especializado",
      "Garantia de conformidade contínua"
    ]
  }
];

const complianceItems = [
  {
    title: "Portaria MS nº 3.523/98",
    description: "Regulamento técnico sobre qualidade do ar interior em ambientes climatizados",
    icon: ClipboardCheck
  },
  {
    title: "Resolução ANVISA RE nº 09/2003",
    description: "Padrões referenciais de qualidade do ar interior em ambientes climatizados",
    icon: Award
  },
  {
    title: "NBR 16401",
    description: "Instalações de ar-condicionado - Sistemas centrais e unitários",
    icon: CheckCircle2
  },
  {
    title: "Lei Municipal específica",
    description: "Adequação às legislações municipais locais sobre qualidade do ar",
    icon: AlertTriangle
  }
];

export function PMOC() {
  return (
    <>
      <Helmet>
        <title>PMOC - Plano de Manutenção, Operação e Controle | Arcgel</title>
        <meta
          name="description"
          content="PMOC certificado pela Arcgel. Garantimos a conformidade legal do seu sistema de climatização com as normas ANVISA e Ministério da Saúde."
        />
      </Helmet>

      <>
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 md:py-32">
          
          <div className="container relative z-10 text-center px-4 pt-20 md:pt-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6">
                <span className="text-white drop-shadow-2xl">PMOC</span>
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent ml-3">
                  Certificado
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-200 mb-8 max-w-2xl mx-auto leading-relaxed">
                Plano de Manutenção, Operação e Controle em total conformidade com as normas ANVISA, 
                Ministério da Saúde e legislações municipais. Garanta a qualidade do ar e a segurança 
                do seu ambiente climatizado.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/contato"
                  className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-full hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center relative overflow-hidden"
                >
                  <span className="relative z-10">Solicitar Orçamento</span>
                  
                  {/* Efeito de brilho */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>

                <a
                  href="https://wa.me/5513991028221"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold text-lg rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center"
                >
                  <MessageCircle className="w-6 h-6" />
                  WhatsApp Direto
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 md:py-32 relative overflow-hidden">
          <div className="container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Serviços <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">PMOC</span>
              </h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Soluções completas para manter seu sistema de climatização em conformidade 
                legal e funcionando com máxima eficiência.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {pmocServices.map((service, index) => (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative rounded-2xl p-8 border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10"
                >
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
                  
                  <div className="relative z-10">
                    <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${service.gradient} mb-6`}>
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {service.name}
                    </h3>
                    
                    <p className="text-slate-300 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <ul className="space-y-3">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-center text-slate-300">
                          <CheckCircle2 className="w-5 h-5 text-cyan-400 mr-3 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance Section */}
        <section className="py-20 md:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-cyan-500/10" />
          
          <div className="container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Conformidade <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Legal Garantida</span>
              </h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Nosso PMOC atende rigorosamente a todas as normas e regulamentações 
                vigentes para sistemas de climatização.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {complianceItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="rounded-xl p-6 border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 text-center shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10"
                >
                  <div className="inline-flex p-3 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 mb-4">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-3">
                    {item.title}
                  </h3>
                  
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <div className="container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-4xl mx-auto"
            >
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Precisa de um
                <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  PMOC Profissional?
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed">
                Garanta a conformidade legal, a qualidade do ar e a eficiência energética 
                do seu sistema de climatização. Nossa equipe especializada está pronta 
                para implementar o PMOC ideal para sua empresa.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link
                  to="/contato"
                  className="group relative px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-full shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3 min-w-[200px] justify-center"
                >
                  <Phone className="w-6 h-6" />
                  Solicitar Orçamento
                  
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>

                <a
                  href="https://wa.me/5513991028221"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group px-10 py-5 bg-white/10 backdrop-blur-sm text-white font-bold text-lg rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center"
                >
                  <MessageCircle className="w-6 h-6" />
                  WhatsApp Direto
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </>
    </>
  );
}
